import { useEffect, useState } from 'react';
import { fetchModelDetails } from '@/features/trainings/api/modelDetailsApi';
import { CURRENT_VERSION_EVENT_ID, fetchVersionsDiff } from '../../api/versionsDiffApi';

/**
 * Data for the Versions Diff modal: the two model-details snapshots (params
 * table) plus the paginated `/versions-diff` response (changed records,
 * distribution, train-set summaries).
 *
 * `toEventId` may be `CURRENT_VERSION_EVENT_ID` ('current'): the diff then
 * compares against the live datasets, and model details are only fetched for
 * the "from" side (the current params come from the usecase, not an event).
 */
export function useVersionsDiff({ clientId, usecaseId, fromEventId, toEventId }) {
  const [details, setDetails] = useState({ from: null, to: null });
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [diff, setDiff] = useState(null);
  const [diffError, setDiffError] = useState(false);
  const [diffLoading, setDiffLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    setPage(1);
  }, [fromEventId, toEventId]);

  useEffect(() => {
    if (!clientId || !fromEventId || !toEventId) return undefined;
    let cancelled = false;
    setDetailsLoading(true);
    Promise.all([
      fromEventId === CURRENT_VERSION_EVENT_ID
        ? Promise.resolve(null)
        : fetchModelDetails({ client_id: clientId, distillation_event_id: fromEventId }),
      toEventId === CURRENT_VERSION_EVENT_ID
        ? Promise.resolve(null)
        : fetchModelDetails({ client_id: clientId, distillation_event_id: toEventId }),
    ])
      .then(([from, to]) => {
        if (cancelled) return;
        setDetails({
          from: from?.success ? from : null,
          to: to?.success ? to : null,
        });
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, fromEventId, toEventId]);

  useEffect(() => {
    if (!clientId || !usecaseId || !fromEventId || !toEventId) return undefined;
    let cancelled = false;
    setDiffLoading(true);
    setDiffError(false);
    fetchVersionsDiff({
      client_id: clientId,
      llm_usecase_id: usecaseId,
      from_distillation_event_id: fromEventId,
      to_distillation_event_id: toEventId,
      page,
      page_size: pageSize,
    })
      .then((result) => {
        if (cancelled) return;
        // A failed request resolves undefined (data_from_server_post swallows
        // the error) — flag it so the modal shows an error state instead of a
        // misleading "no changes between these versions".
        setDiff(result?.success ? result : null);
        setDiffError(!result?.success);
      })
      .finally(() => {
        if (!cancelled) setDiffLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, usecaseId, fromEventId, toEventId, page, pageSize]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  return {
    details,
    detailsLoading,
    diff,
    diffError,
    diffLoading,
    page,
    pageSize,
    setPage,
    setPageSize: handlePageSizeChange,
  };
}
